import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { FilesInputStore } from '@babylonjs/core/Misc/filesInputStore';
import { HDRCubeTexture } from '@babylonjs/core/Materials/Textures/hdrCubeTexture';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import '@babylonjs/core/Loading/Plugins/babylonFileLoader';
// Babylon 9 no longer registers this as a side effect of other modules;
// without it .babylon files that contain instances fail to load.
import '@babylonjs/core/Meshes/instancedMesh';
import '@babylonjs/loaders/glTF';
import '@babylonjs/core/Materials/PBR/pbrMaterial';
import '@babylonjs/core/Materials/PBR/pbrMetallicRoughnessMaterial';
import '@babylonjs/core/Materials/PBR/pbrSpecularGlossinessMaterial';
import '@babylonjs/core/Materials/multiMaterial';
import '@babylonjs/core/Animations/animatable';
import { detectMeshFeatures } from '@designwithfriends/babylon-catalog-features';
import { Alert, Badge, Button, Empty, Space, Spin, Tag, Typography } from 'antd';
import { BorderOutlined, BulbFilled, BulbOutlined, ExclamationCircleOutlined, EyeInvisibleOutlined, EyeOutlined, InboxOutlined } from '@ant-design/icons';
import styles from './index.module.css';

const { Title, Text } = Typography;

const HDR_ROOMS = [`${process.env.PUBLIC_URL}/inspector/room.hdr`, `${process.env.PUBLIC_URL}/inspector/room2.hdr`];

const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(file);
    });

const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.dds', '.ktx', '.tga', '.bmp', '.env', '.basis'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NODE_ARRAYS = ['meshes', 'transformNodes', 'lights', 'cameras', 'skeletons', 'materials', 'multiMaterials'];
const isImagePath = (s) => IMAGE_EXTS.some((ext) => s.endsWith(ext));

// 3dsmax 2025 exporter writes UUID-format `id`/`uniqueId` strings, plus parentIds that
// sometimes reference non-existent parents. Babylon's parent-linking calls parseInt() on
// parentId (corrupts UUIDs) and crashes on `parent.instances[...]` when parent is null
// but parentInstanceIndex is set.
// Single-pass fix on the parsed JSON:
//   1. Map UUIDs owned by some node (as `uniqueId` or `id`) → sequential integers.
//   2. Rewrite owned UUIDs to integers everywhere they appear.
//   3. Drop parentId references that point to non-existent nodes; drop parentInstanceIndex
//      everywhere (the field that crashes null.instances).
//   4. While walking, also register dropped texture files in FilesInputStore under both
//      basename and any path the .babylon JSON references them by.
const fixUniqueIdsAndRegisterTextures = (parsed, fileMap, { stripAllParents = false } = {}) => {
    const ownedUuids = new Set();
    NODE_ARRAYS.forEach((arr) => {
        (parsed[arr] || []).forEach((node) => {
            if (typeof node?.uniqueId === 'string' && UUID_RE.test(node.uniqueId)) ownedUuids.add(node.uniqueId);
            if (typeof node?.id === 'string' && UUID_RE.test(node.id)) ownedUuids.add(node.id);
        });
    });

    const idMap = new Map();
    let counter = 1_000_000;
    ownedUuids.forEach((uuid) => idMap.set(uuid, ++counter));

    const walk = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        if (Array.isArray(obj)) { obj.forEach(walk); return; }

        if ('parentInstanceIndex' in obj) delete obj.parentInstanceIndex;

        if (stripAllParents && 'parentId' in obj) {
            delete obj.parentId;
        } else if (typeof obj.parentId === 'string' && UUID_RE.test(obj.parentId) && !idMap.has(obj.parentId)) {
            delete obj.parentId;
        }

        for (const [key, val] of Object.entries(obj)) {
            if (typeof val !== 'string') {
                if (val && typeof val === 'object') walk(val);
                continue;
            }
            if (idMap.has(val)) {
                obj[key] = idMap.get(val);
            } else {
                const lower = val.toLowerCase();
                if (isImagePath(lower)) {
                    const basename = lower.split(/[/\\]/).pop();
                    const file = fileMap[basename];
                    if (file) FilesInputStore.FilesToLoad[lower] = file;
                }
            }
        }
    };
    walk(parsed);
};

const calcDimensions = (meshes) => {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    meshes.forEach((m) => {
        if (m.getTotalVertices() === 0) return;
        m.computeWorldMatrix(true);
        const bb = m.getBoundingInfo()?.boundingBox;
        if (!bb) return;
        const { minimumWorld: mn, maximumWorld: mx } = bb;
        if (mn.x < minX) minX = mn.x;
        if (mx.x > maxX) maxX = mx.x;
        if (mn.y < minY) minY = mn.y;
        if (mx.y > maxY) maxY = mx.y;
        if (mn.z < minZ) minZ = mn.z;
        if (mx.z > maxZ) maxZ = mx.z;
    });

    if (!isFinite(minX)) return null;
    return {
        width: maxX - minX,
        height: maxY - minY,
        depth: maxZ - minZ,
        belowGround: minY < 0,
    };
};

export function ModelsInspector() {
    const canvasRef = useRef(null);
    const engineRef = useRef(null);
    const sceneRef = useRef(null);
    const hdrTextureRef = useRef(null);
    const highlightMaterialRef = useRef(null);
    const builtinAssetsRef = useRef({ meshes: new Set(), materials: new Set(), textures: new Set() });
    const originalMaterialsRef = useRef(new Map());
    const loadedMeshesRef = useRef([]);
    const folderInputRef = useRef(null);
    const fileInputRef = useRef(null);

    const [meshInfos, setMeshInfos] = useState([]);
    const [hiddenMeshes, setHiddenMeshes] = useState(new Set());
    const [highlightedMeshes, setHighlightedMeshes] = useState(new Set());
    const [selectedMeshId, setSelectedMeshId] = useState(null);
    const [features, setFeatures] = useState(null);
    const [dimensions, setDimensions] = useState(null);
    const [geometry, setGeometry] = useState(null);
    const [showBoundingBox, setShowBoundingBox] = useState(false);
    const [roomIndex, setRoomIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [loadedFileNames, setLoadedFileNames] = useState([]);
    const loadedFile = loadedFileNames.find((n) => /\.(babylon|glb)$/i.test(n)) ?? null;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
        const scene = new Scene(engine);
        scene.clearColor = new Color4(0.08, 0.08, 0.1, 1);

        const hdrTexture = new HDRCubeTexture(HDR_ROOMS[0], scene, 512);
        scene.environmentTexture = hdrTexture;
        hdrTextureRef.current = hdrTexture;

        const highlightMat = new StandardMaterial('inspector-highlight-mat', scene);
        highlightMat.emissiveColor = new Color3(1, 0.85, 0);
        highlightMat.diffuseColor = new Color3(1, 0.85, 0);
        highlightMat.specularColor = new Color3(0, 0, 0);
        highlightMat.disableLighting = true;
        highlightMaterialRef.current = highlightMat;

        const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
        hemi.intensity = 0.7;
        hemi.diffuse = new Color3(1, 0.98, 0.957);

        const dirLight = new DirectionalLight('dir', new Vector3(0.5, -1, 0.5), scene);
        dirLight.intensity = 0.5;
        dirLight.diffuse = new Color3(1, 0.98, 0.957);

        const ground = MeshBuilder.CreateGround('ground', { width: 300, height: 300 }, scene);
        const groundMat = new StandardMaterial('groundMat', scene);
        const floorTex = new Texture(`${process.env.PUBLIC_URL}/inspector/floor.png`, scene);
        floorTex.uScale = 8;
        floorTex.vScale = 8;
        groundMat.diffuseTexture = floorTex;
        groundMat.specularColor = new Color3(0, 0, 0);
        ground.material = groundMat;
        ground.receiveShadows = true;

        const camera = new ArcRotateCamera('cam', -Math.PI / 4, Math.PI / 3, 8, Vector3.Zero(), scene);
        camera.attachControl(canvas, true);
        camera.lowerRadiusLimit = 0.5;
        camera.wheelPrecision = 50;
        camera.minZ = 0.01;

        scene.onPointerDown = (_evt, pickResult) => {
            const picked = pickResult?.pickedMesh;
            if (picked && picked !== ground) {
                setSelectedMeshId(picked.uniqueId);
            }
        };

        builtinAssetsRef.current = {
            meshes: new Set(scene.meshes),
            materials: new Set(scene.materials),
            textures: new Set(scene.textures),
        };

        engine.runRenderLoop(() => {
            if (!scene.isDisposed) scene.render();
        });

        engineRef.current = engine;
        sceneRef.current = scene;

        const handleResize = () => engine.resize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            Object.keys(FilesInputStore.FilesToLoad).forEach((k) => delete FilesInputStore.FilesToLoad[k]);
            engine.dispose();
        };
    }, []);

    const clearLoadedScene = useCallback(() => {
        const scene = sceneRef.current;
        if (!scene) return;
        const builtin = builtinAssetsRef.current;
        [...scene.meshes].filter((m) => !builtin.meshes.has(m)).forEach((m) => m.dispose());
        [...scene.materials].filter((m) => !builtin.materials.has(m)).forEach((m) => m.dispose());
        [...scene.textures].filter((t) => !builtin.textures.has(t)).forEach((t) => t.dispose());
    }, []);

    const loadFiles = useCallback(async (fileList) => {
        const files = Array.from(fileList);
        const modelFile = files.find((f) => f.name.toLowerCase().endsWith('.babylon'))
            ?? files.find((f) => f.name.toLowerCase().endsWith('.glb'));
        if (!modelFile) {
            setError('No .babylon or .glb file found. Drop a model file (plus textures for .babylon).');
            return;
        }
        const isGlb = modelFile.name.toLowerCase().endsWith('.glb');

        setLoading(true);
        setError(null);
        setFeatures(null);
        setDimensions(null);
        setGeometry(null);
        setMeshInfos([]);
        setHiddenMeshes(new Set());
        setHighlightedMeshes(new Set());
        originalMaterialsRef.current.clear();
        setSelectedMeshId(null);
        setLoadedFileNames(files.map((f) => f.name));
        loadedMeshesRef.current = [];

        const scene = sceneRef.current;
        const builtinMeshes = builtinAssetsRef.current.meshes;
        clearLoadedScene();

        try {
            let parsed = null;
            let meshes;

            if (isGlb) {
                // .glb is self-contained (textures embedded) — no JSON patching needed.
                Object.keys(FilesInputStore.FilesToLoad).forEach((k) => delete FilesInputStore.FilesToLoad[k]);
                files.forEach((f) => { FilesInputStore.FilesToLoad[f.name.toLowerCase()] = f; });
                ({ meshes } = await SceneLoader.ImportMeshAsync('', 'file:', modelFile.name.toLowerCase(), scene));
            } else {
                const text = await readFileAsText(modelFile);

                const fileMap = {};
                files.forEach((f) => { fileMap[f.name.toLowerCase()] = f; });

                const tryImport = async (stripAllParents) => {
                    const reparsed = JSON.parse(text);
                    fixUniqueIdsAndRegisterTextures(reparsed, fileMap, { stripAllParents });

                    const patchedFile = new File([JSON.stringify(reparsed)], modelFile.name, { type: 'application/json' });
                    Object.keys(FilesInputStore.FilesToLoad).forEach((k) => delete FilesInputStore.FilesToLoad[k]);
                    files.forEach((f) => { FilesInputStore.FilesToLoad[f.name.toLowerCase()] = f; });
                    FilesInputStore.FilesToLoad[modelFile.name.toLowerCase()] = patchedFile;

                    const result = await SceneLoader.ImportMeshAsync('', 'file:', modelFile.name.toLowerCase(), scene);
                    return { meshes: result.meshes, parsed: reparsed };
                };

                try {
                    ({ meshes, parsed } = await tryImport(false));
                } catch (firstErr) {
                    console.warn('[ModelsInspector] First load attempt failed, retrying with all parents stripped:', firstErr);
                    clearLoadedScene();
                    ({ meshes, parsed } = await tryImport(true));
                }
            }

            const hdr = hdrTextureRef.current;
            meshes.forEach((m) => {
                if (!m.parent && !builtinMeshes.has(m)) {
                    m.position.y += 0.01;
                }
                if (m.material?.getClassName().includes('PBR')) {
                    m.material.environmentTexture = hdr;
                }
            });

            loadedMeshesRef.current = meshes;
            setMeshInfos(meshes.map((m) => ({
                id: m.uniqueId,
                name: m.name,
                mesh: m,
                vertices: m.getTotalVertices(),
                faces: Math.floor(m.getTotalIndices() / 3),
            })));
            // Cabinet feature detection parses babylon JSON — not applicable to .glb.
            setFeatures(parsed ? detectMeshFeatures(parsed) : null);
            setDimensions(calcDimensions(meshes));
            setGeometry(meshes.reduce(
                (acc, m) => ({
                    vertices: acc.vertices + m.getTotalVertices(),
                    faces: acc.faces + Math.floor(m.getTotalIndices() / 3),
                }),
                { vertices: 0, faces: 0 },
            ));
        } catch (e) {
            console.error('[ModelsInspector] Load failed:', e, { inner: e?.innerError ?? e?.cause });
            setError(`Failed to load: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, [clearLoadedScene]);

    const toggleMesh = useCallback((id, mesh) => {
        setHiddenMeshes((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                mesh.setEnabled(true);
            } else {
                next.add(id);
                mesh.setEnabled(false);
            }
            return next;
        });
    }, []);

    const toggleHighlight = useCallback((id, mesh) => {
        const highlightMat = highlightMaterialRef.current;
        const originals = originalMaterialsRef.current;
        if (!highlightMat) return;
        setHighlightedMeshes((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                if (originals.has(id)) {
                    mesh.material = originals.get(id);
                    originals.delete(id);
                }
            } else {
                next.add(id);
                originals.set(id, mesh.material);
                mesh.material = highlightMat;
            }
            return next;
        });
    }, []);

    const toggleBoundingBox = useCallback(() => {
        const next = !showBoundingBox;
        setShowBoundingBox(next);
        loadedMeshesRef.current.forEach((m) => {
            m.showBoundingBox = next;
        });
    }, [showBoundingBox]);

    const switchRoom = useCallback(() => {
        const nextIndex = (roomIndex + 1) % HDR_ROOMS.length;
        const scene = sceneRef.current;
        if (!scene) return;

        hdrTextureRef.current?.dispose();
        const newTex = new HDRCubeTexture(HDR_ROOMS[nextIndex], scene, 512);
        scene.environmentTexture = newTex;
        hdrTextureRef.current = newTex;

        loadedMeshesRef.current.forEach((m) => {
            if (m.material?.getClassName().includes('PBR')) {
                m.material.environmentTexture = newTex;
            }
        });

        setRoomIndex(nextIndex);
    }, [roomIndex]);

    const handleDrop = useCallback(
        async (e) => {
            e.preventDefault();
            setIsDragging(false);

            const items = Array.from(e.dataTransfer.items || []);
            const hasEntries = items.length > 0 && typeof items[0].webkitGetAsEntry === 'function';

            if (!hasEntries) {
                loadFiles(e.dataTransfer.files);
                return;
            }

            // Use FileSystem API to recursively collect files from dropped folders
            const collected = [];
            const processEntry = (entry) =>
                new Promise((resolve) => {
                    if (entry.isFile) {
                        entry.file((f) => { collected.push(f); resolve(); });
                    } else if (entry.isDirectory) {
                        const reader = entry.createReader();
                        const readAll = () => {
                            reader.readEntries(async (entries) => {
                                if (entries.length === 0) { resolve(); return; }
                                await Promise.all(entries.map(processEntry));
                                readAll();
                            });
                        };
                        readAll();
                    } else {
                        resolve();
                    }
                });

            await Promise.all(items.map((item) => {
                const entry = item.webkitGetAsEntry?.();
                return entry ? processEntry(entry) : Promise.resolve();
            }));

            loadFiles(collected);
        },
        [loadFiles]
    );

    return (
        <div className={styles.container}>
            <div className={styles.sidebar}>
                <div className={styles.sidebarHeader}>
                    <Title level={4} style={{ margin: 0, color: '#fff' }}>
                        Models Inspector
                    </Title>
                </div>

                <div
                    className={`${styles.dropZone} ${isDragging ? styles.dropZoneDragging : ''}`}
                    onDrop={handleDrop}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                >
                    <InboxOutlined style={{ fontSize: 32, color: '#7b6ef6' }} />
                    <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, textAlign: 'center' }}>
                        Drop <strong>.babylon folder</strong>, <strong>.glb</strong>, or files
                    </Text>
                    <Space size={6} style={{ marginTop: 4 }}>
                        <Button size="small" onClick={() => folderInputRef.current?.click()}>
                            Browse Folder
                        </Button>
                        <Button size="small" onClick={() => fileInputRef.current?.click()}>
                            Browse Files
                        </Button>
                    </Space>
                    <input
                        ref={folderInputRef}
                        type="file"
                        multiple
                        webkitdirectory=""
                        style={{ display: 'none' }}
                        onChange={(e) => loadFiles(e.target.files)}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => loadFiles(e.target.files)}
                    />
                </div>

                {loadedFileNames.length > 0 && (
                    <div className={styles.section}>
                        <Text className={styles.sectionLabel}>Loaded Files ({loadedFileNames.length})</Text>
                        <div className={styles.fileList}>
                            {loadedFileNames.map((name) => (
                                <Text
                                    key={name}
                                    className={styles.fileName}
                                    style={{ color: name === loadedFile ? '#a78bfa' : 'rgba(255,255,255,0.45)' }}
                                >
                                    {name === loadedFile ? '▸ ' : '  '}{name}
                                </Text>
                            ))}
                        </div>
                    </div>
                )}

                {dimensions && (
                    <div className={styles.section}>
                        <Text className={styles.sectionLabel}>Dimensions</Text>
                        <div className={styles.dimensionGrid}>
                            <DimCell axis="W" value={dimensions.width} />
                            <DimCell axis="H" value={dimensions.height} />
                            <DimCell axis="D" value={dimensions.depth} />
                        </div>
                        {dimensions.belowGround && (
                            <Tag icon={<ExclamationCircleOutlined />} color="warning" style={{ marginTop: 6, fontSize: 11 }}>
                                mesh extends below ground
                            </Tag>
                        )}
                    </div>
                )}

                {geometry && (
                    <div className={styles.section}>
                        <Text className={styles.sectionLabel}>Geometry</Text>
                        <div className={styles.geometryGrid}>
                            <GeomCell label="Vertices" value={geometry.vertices} />
                            <GeomCell label="Faces" value={geometry.faces} />
                        </div>
                    </div>
                )}

                {features && (
                    <div className={styles.section}>
                        <Text className={styles.sectionLabel}>Model Features</Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                            <FeatureRow label="Cabinet Texture" active={features.hasCabinetTexture} />
                            <FeatureRow label="Countertop" active={features.hasCabinetCountertop} />
                            <FeatureRow label="Door Texture" active={features.hasDoorTexture} />
                            <FeatureRow label="Window Texture" active={features.hasWindowTexture} />
                            <FeatureRow label="Background" active={features.hasBackground} />
                        </div>
                    </div>
                )}

                {meshInfos.length > 0 && (
                    <div className={styles.section}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <Text className={styles.sectionLabel}>Meshes</Text>
                            <Badge count={meshInfos.length} style={{ backgroundColor: '#7b6ef6' }} />
                        </div>
                        <div className={styles.meshList}>
                            {meshInfos.map(({ id, name, mesh, vertices, faces }) => (
                                <div
                                    key={id}
                                    className={`${styles.meshRow} ${selectedMeshId === id ? styles.meshRowSelected : ''}`}
                                    onClick={() => setSelectedMeshId(id)}
                                >
                                    <Text ellipsis={{ tooltip: name || '(unnamed)' }} className={styles.meshName}>
                                        {name || '(unnamed)'}
                                    </Text>
                                    <Text className={styles.meshStats} title={`${vertices.toLocaleString()} vertices · ${faces.toLocaleString()} faces`}>
                                        {formatCount(vertices)}v·{formatCount(faces)}f
                                    </Text>
                                    <button
                                        className={`${styles.visibilityBtn} ${highlightedMeshes.has(id) ? styles.visibilityBtnActive : ''}`}
                                        onClick={(e) => { e.stopPropagation(); toggleHighlight(id, mesh); }}
                                        title={highlightedMeshes.has(id) ? 'Remove highlight' : 'Highlight mesh'}
                                    >
                                        {highlightedMeshes.has(id) ? <BulbFilled /> : <BulbOutlined />}
                                    </button>
                                    <button
                                        className={styles.visibilityBtn}
                                        onClick={(e) => { e.stopPropagation(); toggleMesh(id, mesh); }}
                                        title={hiddenMeshes.has(id) ? 'Show mesh' : 'Hide mesh'}
                                    >
                                        {hiddenMeshes.has(id) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {loadedFile && (
                    <div className={styles.section}>
                        <Text className={styles.sectionLabel}>Scene</Text>
                        <Space direction="vertical" size={6} style={{ width: '100%', marginTop: 8 }}>
                            <Button
                                size="small"
                                icon={<BorderOutlined />}
                                onClick={toggleBoundingBox}
                                type={showBoundingBox ? 'primary' : 'default'}
                                style={{ width: '100%', textAlign: 'left' }}
                            >
                                Bounding Box
                            </Button>
                            <Button
                                size="small"
                                onClick={switchRoom}
                                style={{ width: '100%', textAlign: 'left' }}
                            >
                                Switch Room (HDR {roomIndex + 1}/2)
                            </Button>
                        </Space>
                    </div>
                )}

                {error && (
                    <div className={styles.section}>
                        <Alert type="error" message={error} showIcon style={{ fontSize: 12 }} />
                    </div>
                )}
            </div>

            <div className={styles.viewport}>
                <canvas ref={canvasRef} className={styles.canvas} />
                {loading && (
                    <div className={styles.overlay}>
                        <Spin size="large" tip="Loading model…" />
                    </div>
                )}
                {!loadedFile && !loading && (
                    <div className={styles.overlay} style={{ pointerEvents: 'none' }}>
                        <Empty
                            description={
                                <span style={{ color: 'rgba(255,255,255,0.3)' }}>
                                    Drop a .babylon or .glb file to preview
                                </span>
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function formatCount(n) {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return `${n}`;
}

function GeomCell({ label, value }) {
    return (
        <div className={styles.dimCell}>
            <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, textTransform: 'uppercase' }}>{label}</Text>
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                {value.toLocaleString()}
            </Text>
        </div>
    );
}

function DimCell({ axis, value }) {
    return (
        <div className={styles.dimCell}>
            <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, textTransform: 'uppercase' }}>{axis}</Text>
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                {value.toFixed(2)}
            </Text>
        </div>
    );
}

function FeatureRow({ label, active }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: active ? '#52c41a' : 'rgba(255,255,255,0.2)', fontSize: 13 }}>
                {active ? '●' : '○'}
            </span>
            <Text style={{ color: active ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.3)', fontSize: 12 }}>
                {label}
            </Text>
        </div>
    );
}
